import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { ROUTES } from '@/constants';
import { canManageTenant, useAuthStore } from '@/features/auth';
import { cn } from '@/lib/utils';
import { useIsOnprem, useSeatSyncStatus } from '../hooks/useOnprem';
import { describeSyncStatus } from '../lib/syncStatus';

/**
 * App-wide notice on an on-prem install, for its admins, only while the
 * seat sync needs attention (not activated, offline for a day, over the
 * granted seats, suspended, expired, wrong clock). Nothing is shown when all
 * is well, on the SaaS, or to members.
 */
export function SeatSyncBanner() {
	const onprem = useIsOnprem();
	const role = useAuthStore((state) => state.user?.role);
	const status = useSeatSyncStatus(onprem && canManageTenant(role));

	if (!status.data) return null;
	const text = describeSyncStatus(status.data);
	if (!text.alert) return null;

	return (
		<div
			role="status"
			className={cn(
				'mb-4 flex flex-wrap items-start gap-2 rounded-lg border px-3 py-2 text-sm',
				text.tone === 'danger'
					? 'border-destructive/50 bg-destructive/5'
					: 'border-amber-500/50 bg-amber-500/5',
			)}
		>
			<TriangleAlert
				aria-hidden="true"
				className={cn(
					'mt-0.5 size-4 shrink-0',
					text.tone === 'danger'
						? 'text-destructive'
						: 'text-amber-600 dark:text-amber-400',
				)}
			/>
			<p className="min-w-0 flex-1">
				<span className="font-medium">{text.title}.</span>{' '}
				<span className="text-muted-foreground">{text.description}</span>
			</p>
			<Link
				to={`${ROUTES.displays}?tab=seats`}
				className="shrink-0 font-medium underline-offset-4 hover:underline"
			>
				View seats
			</Link>
		</div>
	);
}
